/**
 * KAN-761: диалог "В заказ" для товара каталога: поиск клиента
 * (ClientSearchPicker: имя, клиника, телефон), количество (целое, не меньше 1),
 * "Добавить". После добавления: "Добавлено в корзину: Имя" и кнопка
 * "Открыть корзину" (формирование заказа). Логика: useAddToOrder.
 * AddToOrderFlow: диалог и модалка формирования заказа, которую он открывает.
 */
import { useState } from 'react';
import { CheckCircle2, Loader2, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ClientSearchPicker } from '@/components/emails/ClientSearchPicker';
import { OrderComposeDialog } from '@/components/orders/OrderComposeDialog';
import { parseOrderQuantity, type AddToOrderFlow } from '@/hooks/useAddToOrder';
import { formatPrice } from '@/lib/catalogTree';
import type { Client } from '@/types/client';

function AddForm({ flow }: { flow: AddToOrderFlow }) {
  const [client, setClient] = useState<Client | null>(null);
  const [quantityText, setQuantityText] = useState('1');
  const quantity = parseOrderQuantity(quantityText);
  const canSubmit = client !== null && quantity !== null && !flow.pending;

  return (
    <>
      <div className="space-y-3">
        <div className="space-y-1">
          <span className="text-sm text-gray-500">Клиент</span>
          <ClientSearchPicker selectedId={client?.id ?? null} onSelect={setClient} />
        </div>
        <label className="block space-y-1">
          <span className="text-sm text-gray-500">Количество</span>
          <Input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            aria-label="Количество"
            className="w-28"
            value={quantityText}
            onChange={(e) => setQuantityText(e.target.value)}
          />
        </label>
        {flow.error && (
          <p role="alert" className="text-sm text-red-600">
            {flow.error}
          </p>
        )}
      </div>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={flow.close}>
          Отмена
        </Button>
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            if (client && quantity !== null) flow.submit(client, quantity);
          }}
        >
          {flow.pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Добавить
        </Button>
      </DialogFooter>
    </>
  );
}

function AddedPanel({ flow }: { flow: AddToOrderFlow }) {
  const added = flow.added;
  if (!added) return null;
  return (
    <>
      <p className="flex items-center gap-2 text-sm text-emerald-700">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        {`Добавлено в корзину: ${added.clientName}`}
      </p>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={flow.close}>
          Закрыть
        </Button>
        <Button type="button" onClick={() => flow.openCart(added.orderId)}>
          <ShoppingCart className="mr-1 h-4 w-4" />
          Открыть корзину
        </Button>
      </DialogFooter>
    </>
  );
}

export function AddToOrderDialog({ flow }: { flow: AddToOrderFlow }) {
  const product = flow.product;
  return (
    <Dialog
      open={product !== null}
      onOpenChange={(open) => {
        if (!open) flow.close();
      }}
    >
      <DialogContent className="max-w-md" data-testid="add-to-order-dialog">
        <DialogHeader>
          <DialogTitle>В заказ</DialogTitle>
          <DialogDescription>
            {product ? `${product.name}, ${formatPrice(product.price, product.currency)}` : ''}
          </DialogDescription>
        </DialogHeader>
        {product && (flow.added ? <AddedPanel flow={flow} /> : <AddForm key={String(product.id)} flow={flow} />)}
      </DialogContent>
    </Dialog>
  );
}

/** Диалог "В заказ" и формирование заказа, которое открывает "Открыть корзину". */
export function AddToOrderFlowDialogs({ flow }: { flow: AddToOrderFlow }) {
  return (
    <>
      <AddToOrderDialog flow={flow} />
      <OrderComposeDialog orderId={flow.composeOrderId} onClose={flow.closeCompose} />
    </>
  );
}

/** Кнопка "В заказ"; prominent: крупная основная (карточка товара). */
export function AddToOrderButton({
  onClick,
  label = 'В заказ',
  prominent = false,
}: {
  onClick: () => void;
  label?: string;
  prominent?: boolean;
}) {
  return (
    <Button
      type="button"
      size={prominent ? 'default' : 'sm'}
      variant={prominent ? 'default' : 'outline'}
      className="whitespace-nowrap"
      aria-label={label}
      onClick={onClick}
    >
      <ShoppingCart className="mr-1 h-4 w-4" />В заказ
    </Button>
  );
}
